import { SigningCosmWasmClient } from "@cosmjs/cosmwasm-stargate";
import {
  Context,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { ReactNode } from "react";

import { ICosmosConnector } from "../../interfaces";

export interface CosmJsProviderState {
  address: string | undefined;
  chainId: string | undefined;
  provider: SigningCosmWasmClient | undefined;
  connector: ICosmosConnector;
}

const CosmJsContext: Context<CosmJsProviderState> =
  createContext<CosmJsProviderState>({} as CosmJsProviderState);

interface CosmJsProviderProps {
  children: ReactNode;
  connectors: ICosmosConnector[];
}

export const CosmJsProvider = ({
  children,
  connectors,
}: CosmJsProviderProps) => {
  const [mainConnector, setMainConnector] = useState(connectors[0]);
  const [provider, setProvider] = useState<SigningCosmWasmClient>();
  const [chainId, setChainId] = useState<string>();
  const [address, setAddress] = useState<string>();
  const [content, setContent] = useState<ReactNode>();
  connectors.forEach((connector) => {
    connector.cosmJsProviderHooks = {
      setMainConnector,
      setProvider,
      setChainId,
      setAddress,
      setContent,
    };
  });
  const state = useMemo<CosmJsProviderState>(() => {
    return {
      address,
      chainId,
      provider,
      connector: mainConnector,
    };
  }, [mainConnector, provider, chainId, address]);

  useEffect(() => {
    const listener = () => {
      return mainConnector.activate(chainId);
    };
    window.addEventListener("keplr_keystorechange", listener);

    return () => {
      window.removeEventListener("keplr_keystorechange", listener);
    };
  }, [mainConnector, chainId]);

  return (
    <CosmJsContext.Provider value={state}>
      <>
        {content}
        {children}
      </>
    </CosmJsContext.Provider>
  );
};

export function useCosmJsProvider(): CosmJsProviderState {
  return useContext(CosmJsContext);
}
